<template>
  <div class="rights-record-selector">
    <div class="selector-toolbar">
      <el-select
        v-model="selectedId"
        filterable
        remote
        clearable
        reserve-keyword
        :remote-method="searchRecords"
        :loading="searching"
        placeholder="搜索档案名称、编号或权利人"
        style="flex:1"
        @change="handleSelectionChange">
        <el-option
          v-for="item in recordOptions"
          :key="item.id"
          :label="`${item.record_name}（${item.record_code}）`"
          :value="Number(item.id)"
          :disabled="item.usage_mode === 'exclusive' && Number(item.sound_count) > 0 && Number(item.id) !== Number(value)">
          <div class="record-option">
            <span>{{ item.record_name }}</span>
            <small>
              {{ item.record_code }} · {{ item.rights_holder || '未填写权利人' }} · 已关联 {{ item.sound_count || 0 }} 个资源
            </small>
          </div>
        </el-option>
      </el-select>
      <el-button icon="el-icon-plus" @click="createRecord('shared')">新建可复用档案</el-button>
      <el-button icon="el-icon-document-add" @click="createRecord('exclusive')">仅本资源档案</el-button>
    </div>

    <div v-if="selectedRecord" class="selected-record">
      <div class="record-main">
        <div class="record-title">
          <strong>{{ selectedRecord.record_name }}</strong>
          <el-tag size="mini" :type="selectedRecord.usage_mode === 'shared' ? 'success' : 'info'">
            {{ selectedRecord.usage_mode === 'shared' ? '可复用' : '仅一个资源' }}
          </el-tag>
        </div>
        <p>
          {{ selectedRecord.record_code }}
          · {{ selectedRecord.rights_holder || '未填写权利人' }}
          · 已关联 {{ selectedRecord.sound_count || 0 }} 个资源
          · {{ selectedRecord.evidence_count || 0 }} 份证明
        </p>
      </div>
      <el-button type="text" icon="el-icon-edit" @click="editSelectedRecord">查看 / 编辑档案</el-button>
    </div>

    <div v-else class="selector-empty">
      <i class="el-icon-link"></i>
      先搜索并关联已有档案；确实没有时再新建，避免相同合同或许可被重复录入。
    </div>

    <SoundRightsRecordDialog
      :visible.sync="dialogVisible"
      :record-id="dialogRecordId"
      :initial-usage-mode="dialogUsageMode"
      @saved="handleRecordSaved" />
  </div>
</template>

<script>
import apis from '@/api/index'
import SoundRightsRecordDialog from '@/components/sounds/SoundRightsRecordDialog.vue'

export default {
  name: 'SoundRightsRecordSelector',
  components: {
    SoundRightsRecordDialog
  },
  props: {
    value: {
      type: [Number, String],
      default: null
    }
  },
  /**
   * 创建远程搜索和通用档案弹窗状态。
   *
   * @returns {Object}
   */
  data() {
    return {
      selectedId: this.value ? Number(this.value) : null,
      selectedRecord: null,
      recordOptions: [],
      searching: false,
      dialogVisible: false,
      dialogRecordId: null,
      dialogUsageMode: 'shared'
    }
  },
  watch: {
    /**
     * 父组件切换资源时同步档案 ID 并加载详情。
     *
     * @param {Number|String|null} value 新档案 ID
     * @returns {void}
     */
    value(value) {
      const id = value ? Number(value) : null
      if (id === this.selectedId && this.selectedRecord) return
      this.selectedId = id
      if (id) {
        this.loadSelectedRecord(id)
      } else {
        this.selectedRecord = null
      }
    }
  },
  /**
   * 初始化已有选择或加载最近档案。
   *
   * @returns {Promise<void>}
   */
  async created() {
    if (this.selectedId) {
      await this.loadSelectedRecord(this.selectedId)
    } else {
      await this.searchRecords('')
    }
  },
  methods: {
    /**
     * 远程搜索可关联的版权档案。
     *
     * @param {String} keyword 档案名称、编号或权利人
     * @returns {Promise<void>}
     */
    async searchRecords(keyword) {
      this.searching = true
      try {
        const response = await apis.soundRightsRecord.search({ keyword: keyword || '' })
        if (response.data.code !== 1) throw new Error(response.data.msg || '版权档案搜索失败')
        const list = (response.data.data && response.data.data.list) || []
        this.recordOptions = this.mergeRecordOption(list, this.selectedRecord)
      } catch (error) {
        this.$message.error(error.message || String(error))
      } finally {
        this.searching = false
      }
    },

    /**
     * 读取当前选择档案的完整摘要。
     *
     * @param {Number} recordId 档案 ID
     * @returns {Promise<void>}
     */
    async loadSelectedRecord(recordId) {
      try {
        const response = await apis.soundRightsRecord.read({ record_id: Number(recordId) })
        if (response.data.code !== 1) throw new Error(response.data.msg || '版权档案加载失败')
        this.selectedRecord = response.data.data.record
        this.recordOptions = this.mergeRecordOption(this.recordOptions, this.selectedRecord)
      } catch (error) {
        this.selectedRecord = null
        this.$message.error(error.message || String(error))
      }
    },

    /**
     * 把当前档案补入远程搜索选项并按 ID 去重。
     *
     * @param {Array} list 搜索结果
     * @param {Object|null} record 当前档案
     * @returns {Array}
     */
    mergeRecordOption(list, record) {
      const rows = [...(list || [])]
      if (record && !rows.some(item => Number(item.id) === Number(record.id))) {
        rows.unshift(record)
      }
      return rows
    },

    /**
     * 向父组件提交新的档案选择。
     *
     * @param {Number|null} recordId 选中档案 ID
     * @returns {Promise<void>}
     */
    async handleSelectionChange(recordId) {
      const id = recordId ? Number(recordId) : null
      this.selectedId = id
      if (id) {
        await this.loadSelectedRecord(id)
      } else {
        this.selectedRecord = null
      }
      this.$emit('input', id)
      this.$emit('change', this.selectedRecord)
    },

    /**
     * 打开新建版权档案通用弹窗。
     *
     * @param {String} usageMode shared 或 exclusive
     * @returns {void}
     */
    createRecord(usageMode) {
      this.dialogRecordId = null
      this.dialogUsageMode = usageMode
      this.dialogVisible = true
    },

    /**
     * 打开当前档案编辑弹窗。
     *
     * @returns {void}
     */
    editSelectedRecord() {
      if (!this.selectedRecord) return
      this.dialogRecordId = Number(this.selectedRecord.id)
      this.dialogUsageMode = this.selectedRecord.usage_mode || 'shared'
      this.dialogVisible = true
    },

    /**
     * 新建或修改档案成功后刷新选择并通知父组件。
     *
     * @param {Object} record 最新档案
     * @returns {void}
     */
    handleRecordSaved(record) {
      this.selectedRecord = record
      this.selectedId = Number(record.id)
      this.recordOptions = this.mergeRecordOption(this.recordOptions, record)
      this.$emit('input', this.selectedId)
      this.$emit('change', record)
      this.$emit('record-saved', record)
    }
  }
}
</script>

<style lang="scss" scoped>
.selector-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
}

.record-option {
  display: flex;
  min-width: 520px;
  flex-direction: column;

  small {
    color: #909399;
    font-size: 11px;
  }
}

.selected-record {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-top: 12px;
  padding: 13px 15px;
  border: 1px solid #b3d8ff;
  border-radius: 8px;
  background: #f3f9ff;
}

.record-main {
  min-width: 0;

  p {
    margin: 6px 0 0;
    color: #606266;
    font-size: 12px;
  }
}

.record-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.selector-empty {
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 7px;
  color: #606266;
  background: #f5f7fa;
  font-size: 13px;
}
</style>
